import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Link2,
  Unlink,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Eraser,
  Code,
} from "lucide-react";
import { sanitizeHtml, isEmptyHtml } from "../utils/richText";
import "./Admin.css";
const PROFILE_BY_MODE = { lines: "lines", basic: "basic", full: "full" };
function readBlockTag() {
  try {
    return String(document.queryCommandValue("formatBlock") || "")
      .replace(/[<>]/g, "")
      .toLowerCase();
  } catch {
    return "";
  }
}
function readState() {
  const state = (cmd) => {
    try {
      return document.queryCommandState(cmd);
    } catch {
      return false;
    }
  };
  const block = readBlockTag();
  return {
    bold: state("bold"),
    italic: state("italic"),
    underline: state("underline"),
    ul: state("insertUnorderedList"),
    ol: state("insertOrderedList"),
    h2: block === "h2",
    h3: block === "h3",
    quote: block === "blockquote",
  };
}
function sameState(a, b) {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
}
export default function RichTextEditor({
  value = "",
  onChange,
  mode = "lines",
  placeholder = "",
  minHeight = 120,
}) {
  const profile = PROFILE_BY_MODE[mode] || "lines";
  const hasLists = mode === "basic" || mode === "full";
  const hasBlocks = mode === "full";
  const hasSource = mode === "basic" || mode === "full";

  const ref = useRef(null);
  const lastEmitted = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [active, setActive] = useState({});
  useEffect(() => {
    if (sourceMode) return;
    const el = ref.current;
    if (!el) return;
    if (lastEmitted.current !== value) {
      el.innerHTML = sanitizeHtml(value || "", profile);
      lastEmitted.current = value;
    }
  }, [value, profile, sourceMode]);
  useEffect(() => {
    function onSelectionChange() {
      const el = ref.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return;
      const next = readState();
      setActive((prev) => (sameState(prev, next) ? prev : next));
    }
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);
  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    let html = sanitizeHtml(el.innerHTML, profile);
    if (isEmptyHtml(html)) {
      html = "";
      if (el.innerHTML !== "" && !el.querySelector("li,img,hr,blockquote,h2,h3,h4,pre")) {
        el.innerHTML = "";
      }
    }
    lastEmitted.current = html;
    if (onChange) onChange(html);
  }, [onChange, profile]);
  function focusEditor() {
    const el = ref.current;
    if (!el) return false;
    if (document.activeElement !== el) el.focus();
    try {
      document.execCommand("styleWithCSS", false, false);
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
    }
    return true;
  }
  function exec(command, arg) {
    if (!focusEditor()) return;
    document.execCommand(command, false, arg);
    emit();
    setActive(readState());
  }
  function toggleBlock(tag) {
    exec("formatBlock", readBlockTag() === tag ? "p" : tag);
  }
  function handleLink() {
    const el = ref.current;
    if (!el) return;
    focusEditor();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return;
    const range = sel.getRangeAt(0).cloneRange();
    const anchorEl = sel.anchorNode && (sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement);
    const existing = anchorEl && anchorEl.closest ? anchorEl.closest("a") : null;
    let url = window.prompt(
      "Link address (leave empty to remove the link):",
      existing ? existing.getAttribute("href") || "https://" : "https://"
    );
    if (url === null) return;
    el.focus();
    sel.removeAllRanges();
    sel.addRange(range);
    url = url.trim();
    if (!url || url === "https://") {
      exec("unlink");
      return;
    }
    if (!/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(url)) url = `https://${url}`;
    if (range.collapsed && !existing) {
      const safe = url.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
      exec("insertHTML", `<a href="${safe}">${safe}</a>&nbsp;`);
    } else {
      exec("createLink", url);
    }
  }
  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (text) document.execCommand("insertText", false, text);
    emit();
  }
  function toggleSource() {
    if (sourceMode) {
      const clean = sanitizeHtml(value, profile);
      lastEmitted.current = null;
      if (onChange) onChange(clean);
      setSourceMode(false);
    } else {
      setSourceMode(true);
    }
  }
  const btn = (key, title, Icon, onClick, isActive) => (
    <button
      key={key}
      type="button"
      className={`rte__btn${isActive ? " is-active" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={!!isActive}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <Icon size={15} />
    </button>
  );
  return (
    <div className="rte">
      <div className="rte__toolbar" role="toolbar" aria-label="Text formatting">
        {!sourceMode && (
          <>
            {btn("bold", "Bold (Ctrl+B)", Bold, () => exec("bold"), active.bold)}
            {btn("italic", "Italic (Ctrl+I)", Italic, () => exec("italic"), active.italic)}
            {btn("underline", "Underline (Ctrl+U)", Underline, () => exec("underline"), active.underline)}
            {btn("link", "Add / edit link", Link2, handleLink, false)}
            {btn("unlink", "Remove link", Unlink, () => exec("unlink"), false)}
            {hasBlocks && <span className="rte__sep" />}
            {hasBlocks && btn("h2", "Heading", Heading2, () => toggleBlock("h2"), active.h2)}
            {hasBlocks && btn("h3", "Sub-heading", Heading3, () => toggleBlock("h3"), active.h3)}
            {hasBlocks && btn("quote", "Quote", Quote, () => toggleBlock("blockquote"), active.quote)}
            {hasLists && <span className="rte__sep" />}
            {hasLists && btn("ul", "Bulleted list", List, () => exec("insertUnorderedList"), active.ul)}
            {hasLists && btn("ol", "Numbered list", ListOrdered, () => exec("insertOrderedList"), active.ol)}
            <span className="rte__sep" />
            {btn("clear", "Clear formatting", Eraser, () => exec("removeFormat"), false)}
          </>
        )}
        {hasSource && (
          <button
            type="button"
            className={`rte__btn rte__btn--right${sourceMode ? " is-active" : ""}`}
            title={sourceMode ? "Back to the visual editor" : "Edit the HTML source"}
            aria-pressed={sourceMode}
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleSource}
          >
            <Code size={15} />
            <span className="rte__btn-label">{sourceMode ? "Visual" : "HTML"}</span>
          </button>
        )}
      </div>
      {sourceMode ? (
        <textarea
          className="rte__source"
          value={value}
          onChange={(e) => onChange && onChange(e.target.value)}
          style={{ minHeight }}
          spellCheck={false}
        />
      ) : (
        <div
          ref={ref}
          className="rte__content"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder || "Rich text editor"}
          data-placeholder={placeholder}
          style={{ minHeight }}
          onInput={emit}
          onBlur={emit}
          onPaste={handlePaste}
          onFocus={focusEditor}
        />
      )}
    </div>
  );
}